<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  permuta: { type: Object, required: true },
});

const store = useStore();
const router = useRouter();
const route = useRoute();
const menuOpen = ref(false);
const menuRef = ref(null);
const opening = ref(false);

const STATUSES = ['Novo', 'Contatei', 'Em conversa', 'Fechado'];

const hasPhone = computed(
  () => (props.permuta.phone || '').replace(/\D/g, '').length > 0
);

// SiteUp: abre a negociação DENTRO do Chatwoot — acha/cria o contato do dono pelo
// telefone e navega pra a conversa (em vez de mandar pro WhatsApp externo).
const openConversation = async () => {
  if (opening.value || !hasPhone.value) return;
  opening.value = true;
  try {
    const data = await store.dispatch('permutas/openConversation', {
      phone: props.permuta.phone,
      code: props.permuta.code,
      source: props.permuta.source,
    });
    if (data?.conversation_id) {
      router.push({
        name: 'inbox_conversation',
        params: {
          accountId: route.params.accountId,
          conversation_id: data.conversation_id,
        },
      });
    }
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Não foi possível abrir a conversa.');
  } finally {
    opening.value = false;
  }
};

const formatBRL = value => {
  if (!value && value !== 0) return 'Valor a confirmar';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(value);
};

const locationLabel = computed(() => {
  const parts = [props.permuta.neighborhood, props.permuta.city].filter(
    Boolean
  );
  return parts.join(', ') || 'Local não informado';
});

const photoSrc = computed(() => {
  const photos = props.permuta.photos || [];
  return photos[0] || null;
});

const accepts = computed(() => props.permuta.accepts || []);

const onStatusChange = status => {
  store.dispatch('permutas/updateStatus', {
    source: props.permuta.source,
    code: props.permuta.code,
    status,
  });
  menuOpen.value = false;
};

const closeMenu = e => {
  if (menuRef.value && !menuRef.value.contains(e.target)) {
    menuOpen.value = false;
  }
};

onMounted(() => document.addEventListener('mousedown', closeMenu));
onUnmounted(() => document.removeEventListener('mousedown', closeMenu));
</script>

<template>
  <article
    class="relative flex flex-col rounded-2xl border border-n-weak bg-n-alpha-1 hover:border-n-strong transition-colors duration-150 overflow-hidden"
  >
    <!-- Photo -->
    <div class="relative aspect-video w-full overflow-hidden bg-n-alpha-2">
      <img
        v-if="photoSrc"
        :src="photoSrc"
        :alt="`${permuta.type} em ${permuta.neighborhood}`"
        class="h-full w-full object-cover"
      />
      <div
        v-else
        class="flex h-full w-full items-center justify-center text-n-slate-11 text-xs"
      >
        Sem foto
      </div>
      <!-- Status badge top-right -->
      <div class="absolute right-2 top-2">
        <span
          class="rounded-md px-1.5 py-0.5 text-xs font-semibold bg-n-alpha-3 text-n-slate-12 backdrop-blur-sm"
        >
          {{ permuta.status || 'Novo' }}
        </span>
      </div>
    </div>

    <!-- Body -->
    <div class="flex flex-1 flex-col gap-3 p-4">
      <!-- Code · Type · Location -->
      <div>
        <p class="text-xs font-medium text-n-slate-11">
          <span class="font-semibold text-n-slate-12">{{ permuta.code }}</span>
          <span class="mx-1 text-n-slate-10">·</span>
          {{ permuta.type }}
        </p>
        <div class="mt-1 flex items-center gap-1 text-sm text-n-slate-11">
          <svg
            class="h-3.5 w-3.5 shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <span class="truncate">{{ locationLabel }}</span>
        </div>
        <p class="mt-2 text-lg font-extrabold text-n-slate-12 tabular-nums">
          {{ formatBRL(permuta.price) }}
        </p>
      </div>

      <!-- Aceita em troca -->
      <div class="rounded-xl bg-n-alpha-2 border border-n-weak p-3">
        <div class="flex items-center gap-2 text-n-brand">
          <svg
            class="h-3.5 w-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="m16 3 4 4-4 4" />
            <path d="M20 7H4" />
            <path d="m8 21-4-4 4-4" />
            <path d="M4 17h16" />
          </svg>
          <span class="text-[11px] font-semibold uppercase tracking-wider">Aceita em troca</span>
        </div>
        <div class="mt-2 flex flex-wrap gap-1.5">
          <template v-if="accepts.length > 0">
            <span
              v-for="a in accepts"
              :key="a"
              class="rounded-md bg-n-alpha-3 px-2 py-0.5 text-xs text-n-slate-12"
            >
              {{ a }}
            </span>
          </template>
          <span v-else class="text-xs text-n-slate-11">Aceita permuta</span>
        </div>
      </div>

      <!-- Actions -->
      <div class="mt-auto pt-2 flex items-center gap-1.5">
        <!-- SiteUp: abre a conversa DENTRO do Chatwoot (cria o contato do dono se não existir) -->
        <button
          v-if="hasPhone"
          type="button"
          :disabled="opening"
          class="flex-1 inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-xl bg-n-brand hover:opacity-90 text-white text-sm font-semibold transition-opacity disabled:opacity-60"
          @click="openConversation"
        >
          <svg
            class="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
          </svg>
          {{ opening ? 'Abrindo…' : 'Abrir conversa' }}
        </button>
        <span
          v-else
          class="flex-1 inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-xl bg-n-alpha-2 text-n-slate-10 text-xs font-medium cursor-not-allowed"
        >
          Sem telefone
        </span>

        <!-- Open listing -->
        <a
          v-if="permuta.adUrl"
          :href="permuta.adUrl"
          target="_blank"
          rel="noopener noreferrer"
          title="Abrir anúncio"
          class="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11 transition-colors"
        >
          <svg
            class="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M15 3h6v6" />
            <path d="M10 14 21 3" />
            <path
              d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
            />
          </svg>
        </a>

        <!-- Status menu -->
        <div ref="menuRef" class="relative">
          <button
            title="Marcar status"
            class="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11 transition-colors"
            @click="menuOpen = !menuOpen"
          >
            <svg
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="1" />
              <circle cx="19" cy="12" r="1" />
              <circle cx="5" cy="12" r="1" />
            </svg>
          </button>

          <div
            v-if="menuOpen"
            class="absolute right-0 bottom-11 z-30 w-44 rounded-2xl border border-n-weak bg-n-solid-1 shadow-lg py-1.5"
          >
            <div
              class="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
            >
              Marcar status
            </div>
            <button
              v-for="s in STATUSES"
              :key="s"
              class="w-full text-left flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-2 transition-colors"
              :class="permuta.status === s ? 'text-n-brand' : 'text-n-slate-12'"
              @click="onStatusChange(s)"
            >
              <span
                class="h-1.5 w-1.5 rounded-full"
                :class="permuta.status === s ? 'bg-n-brand' : 'bg-n-slate-9'"
              />
              {{ s }}
              <svg
                v-if="permuta.status === s"
                class="ml-auto h-3.5 w-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>
